import type { WidgetOptions, WidgetConfig, WidgetActionEventDetail } from "./types";
import { trimTrailingSlash } from "./formatting";

export function fallbackConfig(options: WidgetOptions): WidgetConfig {
  return {
    title: options.title || (options.stylePreset === "sotm" ? "SOTM Website Guide" : "Agent chat"),
    launcher_label: options.launcherLabel || (options.stylePreset === "sotm" ? "Ask SOTM" : "Chat"),
    input_placeholder: options.inputPlaceholder || "Ask a question",
    placement: options.position === "left" ? "bottom-left" : "bottom-right",
    guest_greeting: "",
    logged_in_greeting: "",
    not_customer_label: "Not you?",
    not_customer_url: "/account/login",
    nudge: { enabled: false, style: "none" },
    colors: options.primaryColor ? { buttonBackground: options.primaryColor } : {},
    assets: {},
    metadata: {},
    voice: {}
  };
}

export async function loadWidgetConfig(options: WidgetOptions): Promise<WidgetConfig | null> {
  const url = `${trimTrailingSlash(options.runtimeUrl ?? defaultRuntimeUrl)}/v1/apps/${encodeURIComponent(
    options.appId
  )}/widget-config?customer_id=${encodeURIComponent(options.customerId)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${options.runtimeKey}`
    }
  });
  if (!response.ok) {
    return null;
  }
  const payload = (await response.json()) as WidgetConfig;
  return {
    ...fallbackConfig(options),
    ...payload,
    colors: { ...fallbackConfig(options).colors, ...(payload.colors ?? {}) },
    nudge: { ...fallbackConfig(options).nudge, ...(payload.nudge ?? {}) },
    assets: payload.assets ?? {},
    metadata: payload.metadata ?? {},
    voice: payload.voice ?? {}
  };
}

export function recordActionTelemetry(detail: WidgetActionEventDetail, options: WidgetOptions) {
  if (!detail.actionId || !detail.actionType) {
    return;
  }
  try {
    void fetch(`${trimTrailingSlash(options.runtimeUrl ?? defaultRuntimeUrl)}/v1/events/action`, {
      method: "POST",
      keepalive: true,
      headers: {
        Authorization: `Bearer ${options.runtimeKey}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        customer_id: detail.customerId,
        app_id: detail.appId,
        session_id: detail.sessionId,
        action_id: detail.actionId,
        action_type: detail.actionType,
        status: detail.status,
        destination_kind: detail.destinationKind || null,
        url: detail.url || null,
        value_micros: detail.valueMicros ?? null,
        currency: detail.currency ?? null,
        metadata: {
          widget: "storefront",
          page_origin: window.location.origin || null,
          ...detail.metadata
        }
      })
    }).catch(() => {
      // Action telemetry is best-effort and must never block shopper navigation.
    });
  } catch {
    // Action telemetry is best-effort.
  }
}

export function shopperContextPayload(options: WidgetOptions): Record<string, unknown> {
  const raw = options.shopperContext && typeof options.shopperContext === "object" ? options.shopperContext : {};
  const context: Record<string, unknown> = {};
  const authenticated = raw.is_authenticated ?? raw.isAuthenticated;
  if (typeof authenticated === "boolean") {
    context.is_authenticated = authenticated;
  }
  for (const [target, source] of [
    ["source", raw.source],
    ["auth_source", raw.auth_source ?? raw.authSource],
    ["account_url", raw.account_url ?? raw.accountUrl],
    ["order_lookup_url", raw.order_lookup_url ?? raw.orderLookupUrl]
  ] as Array<[string, unknown]>) {
    if (typeof source === "string" && source.trim()) {
      context[target] = source.trim().slice(0, 1000);
    }
  }
  if (window.location.href.startsWith("http")) {
    context.page_url = window.location.href.slice(0, 1000);
  }
  return context;
}

export async function responseMessage(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as { detail?: string };
    return payload.detail || `Runtime request failed with ${response.status}`;
  } catch {
    return `Runtime request failed with ${response.status}`;
  }
}

export const defaultRuntimeUrl = "http://localhost:8001";
